import React from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import _ from 'lodash';
import pluralize from 'pluralize';

import { formatDate } from 'utils/common';
import { useUserContext } from 'common/contexts/UserContext';

interface Props {
  className?: string;
  variant?: 'descriptive' | 'simple';
}

/**
 * Provides a visual representation of the company's total container usage and remaining containers
 * @param props
 * @returns
 */
export const ContainerUsage: React.FC<Props> = (props) => {
  const { className, variant = 'descriptive' } = props;
  const { user } = useUserContext();

  if (!user) {
    return null;
  }

  const containerCreditsLimit = user.company?.container_credits_limit || null;
  const containerCreditsUsed = user.company?.container_credits_used || 0;
  const containerCreditsCycle = user.company?.current_billing_cycle || ['', ''];

  const percentUsage = containerCreditsLimit
    ? (containerCreditsUsed * 100) / containerCreditsLimit
    : 25;

  const exceededLimit = percentUsage >= 100;

  const clampedPercentUsage = Math.min(Math.max(percentUsage, 0), 100);

  let usageCopy: React.ReactNode = null;

  switch (variant) {
    case 'simple':
      usageCopy = `${containerCreditsUsed.toLocaleString()} ${pluralize(
        'container',
        containerCreditsUsed
      )} tracked`;
      if (containerCreditsLimit !== null) {
        usageCopy = `${usageCopy} out of ${containerCreditsLimit.toLocaleString()} available on your plan`;
      }
      break;
    case 'descriptive':
      usageCopy = (
        <>
          {containerCreditsUsed.toLocaleString()}/
          {_.isNil(containerCreditsLimit) ? (
            <AllInclusiveIcon style={{ fontSize: 14 }} />
          ) : (
            containerCreditsLimit?.toLocaleString()
          )}
        </>
      );
      break;
    default:
      break;
  }

  return (
    <div className={`flex flex-col space-y-1 ${className}`}>
      {variant === 'descriptive' && (
        <h4 className="truncate">Container Usage for {user.company?.name}</h4>
      )}
      {containerCreditsCycle.length > 0 &&
        containerCreditsCycle[0] &&
        containerCreditsCycle[1] &&
        !['pioneer', 'trial_end', 'starter'].includes(user.pack) && (
          <h5 className="text-gray-900">
            {`${formatDate(
              containerCreditsCycle[0],
              variant === 'simple' ? 'LL' : 'll'
            )} - ${formatDate(containerCreditsCycle[1], variant === 'simple' ? 'LL' : 'll')}`}
          </h5>
        )}
      <LinearProgress
        className="text-blue-900"
        variant="determinate"
        color={exceededLimit ? 'error' : 'primary'}
        value={clampedPercentUsage}
        classes={{
          root: 'rounded',
        }}
      />
      <div
        className={`w-full flex justify-between ${
          exceededLimit ? 'text-red-900' : 'text-gray-900'
        }`}
      >
        {variant === 'descriptive' && <h5>Usage</h5>}
        <h5>{usageCopy}</h5>
      </div>
    </div>
  );
};
ContainerUsage.displayName = 'ContainerTracking.ContainerUsage';
